import React from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowRightSVG from 'fcmh/theme/assets/icons/arrow-right.svg';
import { useIntl } from 'react-intl';
import messages from '../../../messages';

const resolveLink = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) return resolveLink(val[0]);
  if (val['@id']) return flattenToAppURL(val['@id']);
  if (val.url) return val.url;
  return '';
};

const buildUrlFromScale = (scale, item) => {
  if (!scale) return null;

  const download = scale.download || scale.url || scale['@id'];
  if (!download) return null;

  if (download.startsWith('http://') || download.startsWith('https://')) {
    return flattenToAppURL(download);
  }

  const basePath = scale.base_path || item?.['@id'] || item?.getURL || null;

  if (basePath) {
    const base = flattenToAppURL(basePath).replace(/\/$/, '');
    const dl = download.startsWith('/') ? download : `/${download}`;
    return `${base}${dl}`;
  }

  return flattenToAppURL(download);
};

const pickScale = (scales) => {
  if (!scales) return null;
  return (
    scales.image?.[0] ||
    scales.preview_image?.[0] ||
    (Object.values(scales)[0] && Object.values(scales)[0][0]) ||
    null
  );
};

const getImageURL = (item) => {
  if (!item) return null;

  try {
    const previewImageLink = item?.preview_image_link;
    if (previewImageLink?.image_scales) {
      const scaleFromPreview = pickScale(previewImageLink.image_scales);
      const urlFromPreview = buildUrlFromScale(scaleFromPreview, item);
      if (urlFromPreview) return urlFromPreview;
    }

    const previewLinkScales = item?.image_scales?.preview_image_link;
    if (Array.isArray(previewLinkScales) && previewLinkScales[0]) {
      const urlFromPreviewLink = buildUrlFromScale(previewLinkScales[0], item);
      if (urlFromPreviewLink) return urlFromPreviewLink;
    }

    if (item?.image_scales) {
      const scaleFromItem = pickScale(item.image_scales);
      const urlFromItem = buildUrlFromScale(scaleFromItem, item);
      if (urlFromItem) return urlFromItem;
    }

    const base = flattenToAppURL(item?.['@id'] || item?.getURL || '');
    if (base) {
      return `${base.replace(/\/$/, '')}/@@images/preview_image/preview`;
    }
  } catch (e) {
    console.warn('[FCMH NewsVariation] getImageURL failed', e);
  }

  return null;
};

const NewsVariation = ({
  items = [],
  isEditMode = false,
  headline,
  moreLinkHref,
  moreLinkLabel,
}) => {
  const intl = useIntl();
  const footerHref = resolveLink(moreLinkHref);

  return (
    <section className="news-section" aria-labelledby="news-heading">
      {headline && (
        <h2 id="news-heading" className="news-section__heading">
          {headline}
        </h2>
      )}

      <div className="news-grid">
        {items.map((item, index) => {
          const rawId = item?.['@id'] || item?.getURL || '';
          const itemUrl = rawId ? flattenToAppURL(rawId) : '#';
          const imageSrc = getImageURL(item);

          const date = item?.effective
            ? new Date(item.effective).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
              })
            : '';

          return (
            <article className="news-card" key={rawId || index}>
              <UniversalLink
                href={itemUrl}
                className="news-card__link-wrapper"
                aria-label={`Weiterlesen: ${item?.title || 'Meldung'}`}
                disabled={isEditMode}
              >
                {imageSrc && (
                  <div className="news-card__image-box">
                    <img
                      src={imageSrc}
                      alt={item?.title || ''}
                      className="news-card__image"
                      loading="lazy"
                    />
                    <span aria-hidden className="news-card__image-gradient" />
                  </div>
                )}

                <div className="news-card__body">
                  {date && <span className="news-card__date">{date}</span>}

                  <h3 className="news-card__title">
                    {item?.title || 'Meldung'}
                  </h3>

                  <span className="news-card__link">
                    <Icon name={arrowRightSVG} size="16px" />
                    <span>{intl.formatMessage(messages.readMore)}</span>
                  </span>
                </div>
              </UniversalLink>
            </article>
          );
        })}
      </div>

      {footerHref && moreLinkLabel && (
        <div className="news-section__footer">
          <UniversalLink href={footerHref} className="news-section__more">
            {moreLinkLabel}
          </UniversalLink>
        </div>
      )}
    </section>
  );
};

NewsVariation.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any),
  isEditMode: PropTypes.bool,
  headline: PropTypes.string,
  moreLinkHref: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  moreLinkLabel: PropTypes.string,
};

export default NewsVariation;
